<script>

    import { afterUpdate, createEventDispatcher, tick } from 'svelte'

    import { syncResponsiveLabels } from '../$methods/responsive.table.methods.js'

    import Spinner from './spinner.svelte'
    import Pagination from './pagination.svelte'

    export let items = 0
    export let total = null
    export let metadata = undefined
    export let page = undefined

    export let loading = false
    export let height = null

    export let showToolbarArea = true
    export let showFooterActions = true
    export let showEmptyState = true
    export let showCountBar = true

    export let tableClass = ''
    export let narrowTable = false
    export let skeletonLoading = false
    export let skeletonCols = 5
    export let skeletonRows = 5

    export let embedPagination = false

    export let emptyTitle = 'Sin resultados'
    export let emptySubtitle = ''
    export let emptyIcon = 'inbox'

    export let fillHeight = true

    let tableEl

    const dispatch = createEventDispatcher()

    afterUpdate(async () => {
        if (!tableEl || (loading && !skeletonLoading)) return
        await tick()
        syncResponsiveLabels(tableEl)
    })

    $: heightRaw =
        height != null && String(height).trim() !== '' ? String(height).trim() : ''

    $: totalDisplayed =
        total != null
            ? total
            : metadata != null &&
                typeof metadata === 'object' &&
                metadata.total != null &&
                metadata.total !== undefined
              ? metadata.total
              : null

    $: scrollAreaStyle =
        heightRaw && heightRaw !== 'auto'
            ? `max-height: ${heightRaw}; overflow: auto;`
            : !fillHeight && !heightRaw
              ? 'max-height: 70vh; overflow: auto;'
              : ''

    $: splitMode = !!($$slots?.thead || $$slots?.tbody) && !$$slots?.default

    $: showEmbeddedPagination =
        embedPagination &&
        typeof page !== 'undefined' &&
        metadata != null &&
        typeof metadata === 'object'

    function onPaginationChange(ev) {
        dispatch('change', ev?.detail ?? ev)
    }

</script>

{#if loading && !skeletonLoading}
    <Spinner {loading} />
{/if}

{#if !loading || skeletonLoading}
    <div class="app-table-panel">
        {#if showToolbarArea}
            {#if $$slots.header}
                <div class="app-table-toolbar">
                    <slot name="header"></slot>
                </div>
            {:else if $$slots.toolbar}
                <div class="app-table-toolbar">
                    <slot name="toolbar"></slot>
                </div>
            {/if}
        {/if}

        {#if splitMode}
            <div class="table-container" style={scrollAreaStyle}>
                <table
                    bind:this={tableEl}
                    class="table app-table is-fullwidth {narrowTable ? 'is-narrow' : ''} is-hoverable {tableClass}"
                >
                    {#if $$slots.thead}
                        <slot name="thead"></slot>
                    {:else}
                        <thead>
                            <tr>
                                {#each Array(skeletonCols) as _, i (i)}
                                    <th aria-hidden="true">&nbsp;</th>
                                {/each}
                            </tr>
                        </thead>
                    {/if}

                    {#if loading && skeletonLoading}
                        <tbody>
                            {#each Array(skeletonRows) as _, ri (ri)}
                                <tr aria-hidden="true">
                                    {#each Array(skeletonCols) as _, ci (ci)}
                                        <td>&nbsp;</td>
                                    {/each}
                                </tr>
                            {/each}
                        </tbody>
                    {:else if items > 0}
                        <slot name="tbody"></slot>
                    {:else if showEmptyState}
                        <tbody>
                            <tr>
                                <td colspan="999" class="app-table-empty has-text-centered">
                                    <span class="icon is-large">
                                        <i class="fas fa-{emptyIcon}"></i>
                                    </span>
                                    <p class="title is-5">{emptyTitle}</p>
                                    {#if emptySubtitle}
                                        <p class="subtitle">{emptySubtitle}</p>
                                    {/if}
                                </td>
                            </tr>
                        </tbody>
                    {/if}
                </table>
            </div>
        {:else if loading && skeletonLoading}
            <div class="table-container" style={scrollAreaStyle}>
                <table
                    bind:this={tableEl}
                    class="table app-table is-fullwidth {narrowTable ? 'is-narrow' : ''} is-hoverable {tableClass}"
                >
                    <thead>
                        <tr>
                            {#each Array(skeletonCols) as _, i (i)}
                                <th aria-hidden="true">&nbsp;</th>
                            {/each}
                        </tr>
                    </thead>
                    <tbody>
                        {#each Array(skeletonRows) as _, ri (ri)}
                            <tr aria-hidden="true">
                                {#each Array(skeletonCols) as _, ci (ci)}
                                    <td>&nbsp;</td>
                                {/each}
                            </tr>
                        {/each}
                    </tbody>
                </table>
            </div>
        {:else if items > 0}
            <div class="table-container" style={scrollAreaStyle}>
                <table
                    bind:this={tableEl}
                    class="table app-table is-fullwidth {narrowTable ? 'is-narrow' : ''} is-hoverable {tableClass}"
                >
                    <slot></slot>
                </table>
            </div>
        {:else if showEmptyState}
            <div class="table-container" style={scrollAreaStyle}>
                <table
                    bind:this={tableEl}
                    class="table app-table is-fullwidth {narrowTable ? 'is-narrow' : ''} is-hoverable {tableClass}"
                >
                    <tbody>
                        <tr class="app-table-row--empty">
                            <td colspan="999" class="app-table-empty has-text-centered">
                                <span class="icon is-large">
                                    <i class="fas fa-{emptyIcon}"></i>
                                </span>
                                <p class="title is-5">{emptyTitle}</p>
                                {#if emptySubtitle}
                                    <p class="subtitle">{emptySubtitle}</p>
                                {/if}
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
        {/if}

        {#if showCountBar || $$slots.actions || showEmbeddedPagination || ($$slots.footer && showFooterActions)}
            <footer class="app-table-footer">
                {#if showCountBar}
                    <p class="app-table-count">
                        Mostrando <strong>{items}</strong>
                        {#if totalDisplayed != null && totalDisplayed !== ''}
                            de <strong>{totalDisplayed}</strong>
                        {/if}
                        registros
                    </p>
                {:else}
                    <span></span>
                {/if}
                <div class="is-flex is-align-items-center is-flex-wrap-wrap" style="gap: 0.75rem;">
                    {#if $$slots.actions}
                        <div class="buttons mb-0">
                            <slot name="actions"></slot>
                        </div>
                    {/if}
                    {#if showEmbeddedPagination}
                        <Pagination bind:metadata bind:page on:change={onPaginationChange} />
                    {/if}
                    {#if $$slots.footer && showFooterActions}
                        <slot name="footer"></slot>
                    {/if}
                </div>
            </footer>
        {/if}
    </div>
{/if}
